import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";

import { useAuth } from "../auth/AuthContext";
import { api, type PlayerFamilyProfileSummary } from "./api";
import {
  clearActiveFamilyProfileId,
  familyProfilesInTournament,
  lowestProfileId,
  readActiveFamilyProfileId,
  resolveProfileScope,
  subscribeActiveFamilyProfile,
  writeActiveFamilyProfileId,
  type ProfileScopeResolution,
} from "./playerFamilyProfile";

/**
 * Perfil activo de la cuenta familiar para TODAS las pantallas del jugador
 * (Eduardo 2026-09-29). Cada pantalla llama `usePlayerProfileScope()` (o con
 * `{ tournamentId }` dentro de un torneo), espera `ready`, pide sus datos
 * con `profileId` y pone el selector con `PlayerProfileScopeSwitcher`.
 * Cambiar de perfil en cualquier pantalla actualiza todas: esta pestaña y
 * las demás abiertas.
 */

/** La lista de perfiles se reutiliza entre pantallas; se refresca pasado esto. */
const FAMILY_PROFILES_FRESH_MS = 60_000;

interface FamilyProfilesEntry {
  profiles: PlayerFamilyProfileSummary[] | null;
  loaded: boolean;
  /** La última carga falló (se conserva la lista anterior, si había). */
  failed: boolean;
  fetchedAt: number;
  /** Cuántas cargas han terminado (bien o mal): sirve para saber si la
   * lista se pidió después de abrir una pantalla. */
  generation: number;
}

const EMPTY_ENTRY: FamilyProfilesEntry = { profiles: null, loaded: false, failed: false, fetchedAt: 0, generation: 0 };
const familyCache = new Map<string, FamilyProfilesEntry>();
const familyInFlight = new Map<string, Promise<void>>();
const familyListeners = new Set<() => void>();

function setFamilyEntry(key: string, entry: FamilyProfilesEntry): void {
  familyCache.set(key, entry);
  familyListeners.forEach((listener) => listener());
}

function subscribeFamily(listener: () => void): () => void {
  familyListeners.add(listener);
  return () => {
    familyListeners.delete(listener);
  };
}

/** Carga (o reutiliza) los perfiles de la cuenta y en qué torneos juega cada uno. */
export function ensureFamilyProfiles(token: string, userId: number, force = false): Promise<void> {
  const key = String(userId);
  const pending = familyInFlight.get(key);
  if (pending) return pending;
  const current = familyCache.get(key);
  if (!force && current?.profiles && Date.now() - current.fetchedAt < FAMILY_PROFILES_FRESH_MS) {
    return Promise.resolve();
  }
  const promise = api.dashboards
    .playerFamilyProfiles(token)
    .then((response) => {
      const profiles = response.data.profiles ?? [];
      const generation = (familyCache.get(key)?.generation ?? 0) + 1;
      setFamilyEntry(key, { profiles, loaded: true, failed: false, fetchedAt: Date.now(), generation });
      // Sin perfil activo guardado todavía: se guarda el de omisión (id más
      // bajo, el mismo del servidor). Nunca se pisa uno que ya eligieron…
      // salvo que ya no sea de la cuenta (Eduardo 2026-10-04: «Pasar a su
      // propia cuenta» saca a una jugadora de la cuenta de otra persona; su
      // id guardado daba «Ese perfil no pertenece a tu cuenta» al abrir).
      const stored = readActiveFamilyProfileId(userId);
      if (stored === null || !profiles.some((profile) => profile.id === stored)) {
        const fallback = lowestProfileId(profiles);
        if (fallback) writeActiveFamilyProfileId(userId, fallback);
        else if (stored !== null) clearActiveFamilyProfileId(userId);
      }
    })
    .catch(() => {
      // Sin red: se queda la lista anterior (si había) y las pantallas usan
      // el perfil guardado (ver resolveProfileScope).
      const previous = familyCache.get(key);
      setFamilyEntry(key, {
        profiles: previous?.profiles ?? null,
        loaded: true,
        failed: true,
        fetchedAt: previous?.fetchedAt ?? 0,
        generation: (previous?.generation ?? 0) + 1,
      });
    })
    .finally(() => {
      familyInFlight.delete(key);
    });
  familyInFlight.set(key, promise);
  return promise;
}

/** Solo para pruebas: olvida la lista cargada. */
export function resetFamilyProfilesCacheForTests(): void {
  familyCache.clear();
  familyInFlight.clear();
}

/** Perfil activo guardado de la cuenta, sincronizado entre pantallas y pestañas. */
export function useActiveFamilyProfileId(): number | null {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const subscribe = useCallback(
    (onChange: () => void) => subscribeActiveFamilyProfile(userId, onChange),
    [userId],
  );
  const getSnapshot = useCallback(
    () => (userId === null ? null : readActiveFamilyProfileId(userId)),
    [userId],
  );
  return useSyncExternalStore(subscribe, getSnapshot, () => null);
}

function useFamilyProfilesEntry(): FamilyProfilesEntry {
  const { token, user } = useAuth();
  const userId = user?.id ?? null;
  const getSnapshot = useCallback(
    () => (userId === null ? EMPTY_ENTRY : familyCache.get(String(userId)) ?? EMPTY_ENTRY),
    [userId],
  );
  const entry = useSyncExternalStore(subscribeFamily, getSnapshot, () => EMPTY_ENTRY);
  useEffect(() => {
    if (!token || userId === null) return;
    void ensureFamilyProfiles(token, userId);
  }, [token, userId]);
  return entry;
}

/** Perfiles de la cuenta (con sus torneos). `profiles` es null mientras no se sabe. */
export function useFamilyProfiles(): { profiles: PlayerFamilyProfileSummary[] | null; loaded: boolean } {
  const entry = useFamilyProfilesEntry();
  return { profiles: entry.profiles, loaded: entry.loaded };
}

export interface PlayerProfileScope extends ProfileScopeResolution {
  /** Cambia el perfil activo de la cuenta: lo ven todas las pantallas. */
  setProfileId: (profileId: number) => void;
}

export interface PlayerProfileScopeOptions {
  /** Pantalla de un torneo: el selector solo ofrece a quienes juegan ESE torneo. */
  tournamentId?: number | null;
  /** Perfil que trae la liga (gana al activo si es de la cuenta). */
  preferredProfileId?: number | null;
}

export function usePlayerProfileScope(options: PlayerProfileScopeOptions = {}): PlayerProfileScope {
  const { token, user } = useAuth();
  const activeProfileId = useActiveFamilyProfileId();
  const entry = useFamilyProfilesEntry();
  const { profiles, loaded, failed, generation } = entry;
  const tournamentId = options.tournamentId ?? null;
  const preferredProfileId = options.preferredProfileId ?? null;
  const userId = user?.id ?? null;

  // Pantalla de un torneo con una lista cargada ANTES de abrirla que no trae
  // ese torneo (p. ej. el hijo se inscribió hace unos segundos): se vuelve a
  // pedir UNA vez antes de decidir; si no, se caía al primer hijo de la cuenta.
  const [opened, setOpened] = useState(() => ({ tournamentId, generation }));
  if (opened.tournamentId !== tournamentId) {
    setOpened({ tournamentId, generation });
  }
  const tournamentMissing =
    tournamentId != null &&
    Number.isFinite(tournamentId) &&
    profiles !== null &&
    familyProfilesInTournament(profiles, tournamentId).length === 0;
  const listPredatesScreen = profiles !== null && !failed && generation <= opened.generation;
  const awaitingRefresh = tournamentMissing && listPredatesScreen;
  // El hijo activo es de la cuenta pero la lista (vieja) no lo trae en este
  // torneo, donde sí juega un hermano: se usa al hermano y se vuelve a pedir
  // la lista sin frenar la pantalla; si el activo ya juega, se cambia a él.
  const activeMissing =
    tournamentId != null &&
    Number.isFinite(tournamentId) &&
    activeProfileId !== null &&
    (profiles ?? []).some((profile) => profile.id === activeProfileId) &&
    !familyProfilesInTournament(profiles ?? [], tournamentId).some((profile) => profile.id === activeProfileId);
  const shouldRefresh = listPredatesScreen && (tournamentMissing || activeMissing);
  useEffect(() => {
    if (!shouldRefresh || !token || userId === null) return;
    void ensureFamilyProfiles(token, userId, true);
  }, [shouldRefresh, token, userId]);

  const resolution = useMemo(
    () =>
      resolveProfileScope({
        familyProfiles: profiles,
        loaded,
        activeProfileId,
        tournamentId,
        preferredProfileId,
        listFailed: failed,
        awaitingRefresh,
      }),
    [activeProfileId, awaitingRefresh, failed, loaded, preferredProfileId, profiles, tournamentId],
  );
  const setProfileId = useCallback(
    (profileId: number) => {
      if (userId === null) return;
      writeActiveFamilyProfileId(userId, profileId);
    },
    [userId],
  );
  return useMemo(() => ({ ...resolution, setProfileId }), [resolution, setProfileId]);
}
